/**
 * Formatting helpers used on both sides: the server for notification subjects
 * and log lines, the client for everything a human reads.
 */
import { DEFAULT_LOCALE } from './constants.js';
const BYTE_UNITS = ['B', 'kB', 'MB', 'GB', 'TB'];
/**
 * Human byte sizes. Uses binary steps (what the OS reports) with short labels,
 * because "4.7 GB free of 5 GB" is what the admin expects to read.
 */
export function formatBytes(bytes, locale = DEFAULT_LOCALE) {
    if (!Number.isFinite(bytes) || bytes <= 0) {
        return `0 ${BYTE_UNITS[0]}`;
    }
    const exponent = Math.min(BYTE_UNITS.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
    const value = bytes / 1024 ** exponent;
    // Whole bytes never need decimals; larger units get one digit under 100.
    const fractionDigits = exponent === 0 ? 0 : value < 100 ? 1 : 0;
    const formatted = new Intl.NumberFormat(locale, {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    }).format(value);
    return `${formatted} ${BYTE_UNITS[exponent]}`;
}
/**
 * `852684` -> `852.6k` once a number is big enough that the raw digits stop
 * being readable at a glance (character health/mana pools, mainly) —
 * truncated (not rounded) to one decimal, so it never overstates the value.
 * Anything smaller prints as-is, with locale-appropriate thousands grouping.
 */
export function formatCompactNumber(value, locale = DEFAULT_LOCALE) {
    if (Math.abs(value) >= 100_000) {
        const thousands = Math.trunc(value / 100) / 10;
        return `${thousands.toFixed(1)}k`;
    }
    return new Intl.NumberFormat(locale).format(value);
}
/** `0.873` -> `87 %` (with the non-breaking space French typography wants). */
export function formatPercent(ratio, locale = DEFAULT_LOCALE) {
    const clamped = Math.min(1, Math.max(0, Number.isFinite(ratio) ? ratio : 0));
    return new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 0 }).format(clamped);
}
/** Full date and time, e.g. `3 septembre 2026 à 17:06`. */
export function formatDateTime(value, locale = DEFAULT_LOCALE) {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime()))
        return '';
    return new Intl.DateTimeFormat(locale, {
        dateStyle: 'long',
        timeStyle: 'short',
    }).format(date);
}
/** Compact date and time for dense lists, e.g. `03/09/2026 17:06`. */
export function formatShortDateTime(value, locale = DEFAULT_LOCALE) {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime()))
        return '';
    return new Intl.DateTimeFormat(locale, {
        dateStyle: 'short',
        timeStyle: 'short',
    }).format(date);
}
export function formatDate(value, locale = DEFAULT_LOCALE) {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime()))
        return '';
    return new Intl.DateTimeFormat(locale, { dateStyle: 'long' }).format(date);
}
const RELATIVE_STEPS = [
    ['second', 60],
    ['minute', 60],
    ['hour', 24],
    ['day', 7],
    ['week', 4.348],
    ['month', 12],
    ['year', Number.POSITIVE_INFINITY],
];
/**
 * "il y a 5 minutes" / "5 minutes ago". Walks up the units until the value
 * fits, so the caller never has to pick one.
 */
export function formatRelativeTime(value, locale = DEFAULT_LOCALE, now = new Date()) {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime()))
        return '';
    const formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    let delta = (date.getTime() - now.getTime()) / 1000;
    for (const [unit, span] of RELATIVE_STEPS) {
        if (Math.abs(delta) < span) {
            return formatter.format(Math.round(delta), unit);
        }
        delta /= span;
    }
    return formatter.format(Math.round(delta), 'year');
}
/** `95` -> `1:35`, for audio and video durations. */
export function formatDuration(totalSeconds) {
    if (!Number.isFinite(totalSeconds) || totalSeconds < 0)
        return '0:00';
    const seconds = Math.floor(totalSeconds % 60);
    const minutes = Math.floor((totalSeconds / 60) % 60);
    const hours = Math.floor(totalSeconds / 3600);
    const paddedSeconds = String(seconds).padStart(2, '0');
    if (hours > 0)
        return `${hours}:${String(minutes).padStart(2, '0')}:${paddedSeconds}`;
    return `${minutes}:${paddedSeconds}`;
}
/**
 * Initials for the fallback avatar: at most two letters, from word starts.
 * Falls back to `?` so the avatar is never an empty circle.
 */
export function initialsOf(name) {
    // Split on spaces and separators, but not on hyphens: "Yann-vai Gouezou"
    // should read YG, not YV.
    const words = name
        .trim()
        .split(/[\s._]+/)
        .filter(Boolean);
    if (words.length === 0)
        return '?';
    const letters = words.slice(0, 2).map((word) => [...word][0] ?? '');
    return letters.join('').toUpperCase() || '?';
}
/**
 * A stable hue per name, so a user's fallback avatar keeps the same colour
 * everywhere without storing anything.
 */
export function hueFromString(value) {
    let hash = 0;
    for (let index = 0; index < value.length; index += 1) {
        hash = (hash * 31 + value.charCodeAt(index)) % 360;
    }
    return hash;
}
//# sourceMappingURL=format.js.map